import React, { useState } from 'react';

const LOGO_URL = "https://firebasestorage.googleapis.com/v0/b/surveyappreact.firebasestorage.app/o/default%2Flogo_pb.svg?alt=media&token=e0035fbc-fa3e-4b6c-8ae5-81eac333c8e4";

const SurveyListSkeleton = ({ viewMode = 'tiles' }) => {
  const [logoLoaded, setLogoLoaded] = useState(false);

  // Minimalistic skeleton with subtle, calm animation
  const SkeletonElement = ({ className, animate = true }) => (
    <div 
      className={`${animate ? 'bg-gray-100 animate-shimmer-subtle' : 'bg-gray-100'} ${className}`} 
      style={{ transform: 'translateZ(0)' }}
    />
  );

  return (
    <div className="absolute inset-0 bg-white z-[41]">
      <div className="fixed left-0 top-0 z-50 flex h-16 w-[60px] items-center justify-center pl-3 pointer-events-none" aria-hidden="true">
        <div className="relative h-8 w-8 overflow-hidden rounded-lg bg-surface-muted">
          <img
            src={LOGO_URL}
            alt=""
            className={`absolute inset-0 h-full w-full grayscale transition-opacity duration-200 ${logoLoaded ? 'opacity-45' : 'opacity-0'}`}
            onLoad={() => setLogoLoaded(true)}
          />
        </div>
      </div>
      <div className="flex min-h-screen relative bg-white">
        {/* Sidebar Skeleton - matching exact positioning */}
        <div className="hidden fixed left-0 top-16 bg-white transition-all duration-300 z-30 h-[calc(100vh-64px)] w-72 sm:block">
          <div className="h-full flex flex-col">
            {/* Middle Section - Scrollable */}
            <div className="flex-1">
              <div className="px-4 py-0">
                {/* Workspace Header - matches real component exactly */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-2 pl-3 items-center">
                      <img src="/images/workspace-grid.svg" alt="" className="opacity-50" />
                      <span className="text-sm text-gray-400 tracking-[0.1px]" style={{ fontFamily: "'Inter', sans-serif" }}>Workspaces</span>
                    </div>
                  <button className="text-gray-400 w-7 h-7 border border-gray-200 bg-white flex items-center justify-center rounded-md">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </button>
                </div>

                {/* Workspace Items - matches WorkspaceItem component */}
                <div className="space-y-1">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${i === 1 ? 'bg-surface-selected' : ''}`}>
                      <div className="flex items-center gap-2">
                        <SkeletonElement className="h-4 w-4 rounded" />
                        <SkeletonElement className={`h-4 rounded ${i === 1 ? 'w-32' : i === 2 ? 'w-20' : 'w-16'}`} />
                      </div>
                      <SkeletonElement className="h-3 w-4 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer Section - matches ResponseCounter compact variant */}
            <div className="flex-shrink-0 p-4 pt-0 pb-[11px]">
              <div className="space-y-[8px]">
                {/* Response Counter - matches compact selected surface */}
                <div className="my-[8px] px-4 py-3 bg-surface-selected rounded-xl">
                  {/* Plan header */}
                  <div className="flex items-center justify-between mb-2">
                    <SkeletonElement className="h-3 w-16 rounded" />
                    <SkeletonElement className="h-3 w-14 rounded" />
                  </div>
                  {/* Response limit text */}
                  <SkeletonElement className="h-3 w-32 rounded mb-2" />
                  {/* Counter */}
                  <div className="flex items-baseline mb-2">
                    <SkeletonElement className="h-5 w-6 rounded mr-1" />
                    <SkeletonElement className="h-4 w-12 rounded" />
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-1">
                    <div className="h-full w-[10%] bg-gray-300 rounded-full" />
                  </div>
                  {/* Reset text */}
                  <SkeletonElement className="h-3 w-24 rounded" />
                </div>

                {/* Upgrade Button - matches UpgradeButton component */}
                <div className="w-full px-4 py-[1px] mt-[8px] rounded-lg flex items-center justify-between bg-transparent">
                  <SkeletonElement className="h-4 w-36 rounded" />
                  <SkeletonElement className="h-4 w-4 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="ml-0 flex-1 min-w-0 transition-all duration-300 relative sm:ml-72">
          <div className="w-full min-h-full bg-white py-32 sm:py-[66px] px-3 sm:px-6">
            <div className="w-full max-w-[1400px] mx-auto sm:px-4">
              {/* Header Section - Workspace name: text-xl sm:text-2xl = 20px/24px line height ~28px/32px */}
              <div className="hidden sm:block mb-4 sm:mb-6">
                <div className="flex items-center justify-between min-h-[32px]">
                  <div className="flex items-center gap-2">
                    <SkeletonElement className="h-[24px] sm:h-[28px] w-44 rounded" />
                    <div className="w-[20px] h-[20px] flex items-center justify-center">
                      <SkeletonElement className="w-4 h-4 rounded" />
                    </div>
                  </div>
                </div>
                
                {/* Create Form Button and Controls */}
                <div className="hidden sm:block mb-6 mt-4">
                  <div className="max-w-[1720px] mx-auto px-2 sm:px-0">
                    <div className="flex items-center justify-between">
                      {/* Create a form button - matches actual black button */}
                      <div className="h-10 w-44 bg-gray-200 rounded-lg animate-shimmer-subtle" />

                      <div className="flex items-center gap-3">
                        {/* Sort dropdown - matches: flex items-center gap-2 px-3 py-2 text-sm border rounded-lg */}
                        <div className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg">
                          <SkeletonElement className="w-4 h-4 rounded" />
                          <SkeletonElement className="w-[85px] h-[14px] rounded" />
                          <SkeletonElement className="w-4 h-4 rounded" />
                        </div>

                        {/* View mode toggle */}
                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white">
                          <div className="p-2.5 bg-blue-50 border-r border-gray-200">
                            <svg className="w-4 h-4 text-blue-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="7" height="7"/>
                              <rect x="14" y="3" width="7" height="7"/>
                              <rect x="3" y="14" width="7" height="7"/>
                              <rect x="14" y="14" width="7" height="7"/>
                            </svg>
                          </div>
                          <div className="p-2.5">
                            <svg className="w-4 h-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="4" y1="6" x2="20" y2="6"/>
                              <line x1="4" y1="12" x2="20" y2="12"/>
                              <line x1="4" y1="18" x2="20" y2="18"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Survey Content - Conditional based on viewMode */}
              {viewMode === 'tiles' ? (
                // Grid View Skeleton
                <div
                  className="grid gap-4 sm:gap-6 max-w-[1720px] mx-auto px-2 sm:px-0"
                  style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))' }}
                >
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className="group relative rounded-[16px] border border-gray-200 bg-white shadow-sm w-full min-w-0 h-[260px] sm:h-[260px] overflow-hidden"
                    >
                      {/* Header Section - matches survey card header */}
                      <div className="absolute inset-x-0 top-0 h-[150px] rounded-t-[16px] overflow-hidden">
                        <SkeletonElement className="h-full w-full rounded-t-[16px]" />
                      </div>

                      {/* Survey Content Section - matches real card content */}
                      <div className="absolute inset-x-0 bottom-0 h-[110px] bg-white rounded-b-[16px] p-3 sm:p-4 border-t border-gray-100/50">
                        <div className="flex flex-col h-full justify-between">
                          {/* Survey Title */}
                          <div>
                            <SkeletonElement className={`h-[15px] sm:h-[16px] rounded mb-1 ${i % 3 === 0 ? 'w-4/5' : i % 3 === 1 ? 'w-3/4' : 'w-2/3'}`} />
                            <SkeletonElement className={`h-[15px] sm:h-[16px] rounded ${i % 2 === 0 ? 'w-1/2' : 'w-2/5'}`} />
                          </div>

                          {/* Stats and Actions */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 sm:gap-5">
                              {/* Views */}
                              <div className="flex items-center gap-1.5 sm:gap-2">
                                <div className="p-1 bg-gray-100 rounded-full">
                                  <SkeletonElement className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded" />
                                </div>
                                <SkeletonElement className="h-3 w-4 rounded" />
                              </div>
                              {/* Responses */}
                              <div className="flex items-center gap-1.5 sm:gap-2">
                                <div className="p-1 bg-gray-100 rounded-full">
                                  <SkeletonElement className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded" />
                                </div>
                                <SkeletonElement className="h-3 w-4 rounded" />
                              </div>
                            </div>
                            
                            {/* 3-dots menu button */}
                            <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-gray-50 rounded-full">
                              <SkeletonElement className="w-4 h-4 rounded" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // List View Skeleton
                <div className="w-full max-w-[1000px] mx-auto">
                  {/* Table Header */}
                  <div className="hidden sm:grid grid-cols-12 gap-2 p-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 mb-2">
                    <div className="col-span-5">
                      <SkeletonElement className="h-3 w-8 rounded" />
                    </div>
                    <div className="col-span-2 text-center">
                      <SkeletonElement className="h-3 w-10 rounded mx-auto" />
                    </div>
                    <div className="col-span-2 text-center">
                      <SkeletonElement className="h-3 w-16 rounded mx-auto" />
                    </div>
                    <div className="col-span-2 text-center">
                      <SkeletonElement className="h-3 w-10 rounded mx-auto" />
                    </div>
                    <div className="col-span-1 text-center">
                      <SkeletonElement className="h-3 w-12 rounded mx-auto" />
                    </div>
                  </div>

                  {/* Table Rows */}
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                        {/* Survey Name with thumbnail */}
                        <div className="col-span-6 sm:col-span-5 flex items-center gap-2 sm:gap-3">
                          <SkeletonElement className="w-10 h-10 rounded-lg flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <SkeletonElement className="h-4 w-3/4 rounded mb-1" />
                            <SkeletonElement className="h-3 w-1/2 rounded" />
                          </div>
                        </div>

                        {/* Views */}
                        <div className="col-span-3 sm:col-span-2 flex items-center justify-center">
                          <SkeletonElement className="h-4 w-6 rounded" />
                        </div>

                        {/* Responses */}
                        <div className="col-span-3 sm:col-span-2 flex items-center justify-center">
                          <SkeletonElement className="h-4 w-6 rounded" />
                        </div>

                        {/* Status */}
                        <div className="hidden sm:flex sm:col-span-2 items-center justify-center">
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-50">
                            <SkeletonElement className="w-1.5 h-1.5 rounded-full" animate={false} />
                            <SkeletonElement className="h-3 w-8 rounded" />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                          <SkeletonElement className="w-6 h-6 rounded-full" animate={false} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyListSkeleton; 